import { CheckIcon, ChevronDownIcon, RotateCcwIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { sourceTitle } from './event-copy';

/**
 * The small pieces History and Catalog changes share: numbers set in the
 * mono face, a before-and-after, a labelled filter, the banner a revert
 * leaves, and a list of sources.
 */

/** Text with every number in it set in the mono face, as the console sets figures. */
export function Figures({ text, className }: { readonly text: string; readonly className?: string }) {
  const parts = text.split(/(\d(?:[\d.,:]*\d)?%?)/).filter((part) => part !== '');
  return (
    <span className={className}>
      {parts.map((part, index) =>
        /^\d/.test(part) ? (
          <span key={index} className="font-mono text-[12.5px] tabular-nums">
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  );
}

/** "98.4 kcal → 221 kcal": the old value quiet, the new one weighted. */
export function BeforeAfter({ before, after }: { readonly before: string; readonly after: string }) {
  return (
    <span className="min-w-0 text-pretty">
      <Figures text={before} className="text-muted-foreground" />
      <span aria-hidden="true" className="px-1 text-muted-foreground">
        →
      </span>
      <span className="sr-only"> to </span>
      <Figures text={after} className="font-medium text-foreground" />
    </span>
  );
}

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

/** A select whose trigger says what it filters: "Source All sources". */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly FilterOption[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} size="sm" className="h-8 gap-1.5 rounded-lg text-[13px] font-medium">
        <span className="font-normal text-muted-foreground">{label}</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** What just happened, said once: a save or a revert landing. */
export function StatusBanner({
  tone = 'ok',
  children,
  action,
}: {
  readonly tone?: 'ok' | 'mute';
  readonly children: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3.5 py-1.5 text-[13px]',
        tone === 'ok' ? 'bg-ok-bg text-foreground' : 'bg-mute-bg text-foreground',
      )}
    >
      {tone === 'ok' ? (
        <CheckIcon aria-hidden="true" className="size-4 shrink-0 text-ok-fg" />
      ) : (
        <RotateCcwIcon aria-hidden="true" className="size-4 shrink-0 text-mute-fg" />
      )}
      <span className="min-w-0 flex-1 text-pretty">{children}</span>
      {action}
    </div>
  );
}

/** Numbered sources, each a link when it is one. */
export function SourceList({
  sources,
  className,
  id,
}: {
  readonly sources: readonly string[];
  readonly className?: string;
  readonly id?: string;
}) {
  return (
    <ol id={id} className={cn('flex flex-col gap-0.5', className)}>
      {sources.map((source, index) => {
        const { title, href } = sourceTitle(source);
        return (
          <li key={`${source}-${String(index)}`} className="flex min-w-0 items-baseline gap-2 text-[12.5px]">
            <span className="font-mono text-info-fg">[{index + 1}]</span>
            {href === null ? (
              <span className="min-w-0 font-medium break-words text-foreground">{title}</span>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="min-w-0 font-medium break-all text-foreground underline-offset-2 hover:underline"
              >
                {title}
              </a>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** "4 sources" with a chevron, opening the list under it. */
export function SourcesButton({
  count,
  open,
  onToggle,
  controls,
  label,
}: {
  readonly count: number;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly controls: string;
  readonly label?: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className="inline-flex h-8 items-center gap-1 rounded-md px-1.5 text-[13px] font-medium text-info-fg hover:underline"
    >
      {label ?? `${String(count)} ${count === 1 ? 'source' : 'sources'}`}
      <ChevronDownIcon
        aria-hidden="true"
        className={cn('size-3.5 transition-transform', open && 'rotate-180')}
      />
    </button>
  );
}
